/**
 * AdminShell - Mantis Free Dashboard layout.
 *
 * Matches Mantis patterns:
 * - Sidebar: borderRight active indicator on nav items, group labels
 * - AppBar: admin context on left, user section on right
 * - Content: grey background, padded content area
 */

import type { FC } from 'react';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  IconButton,
  Divider,
  useMediaQuery,
  Stack,
  type Theme,
  MenuIcon,
  ChevronLeftIcon,
  DashboardIcon,
  PeopleIcon,
  StoreIcon,
  CollectionsIcon,
  ContentCutIcon,
  CreditCardIcon,
  PaymentIcon,
  StarIcon,
  HealthAndSafetyIcon,
  ShieldIcon,
  HistoryIcon,
  LogoutOutlinedIcon,
} from '@/shared/ui/mui';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { adminTokens } from '../styles/admin.tokens';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navGroups: NavGroup[] = [
  {
    label: 'Navigation',
    items: [
      { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
    ],
  },
  {
    label: 'Management',
    items: [
      { label: 'Providers', path: '/admin/providers', icon: <PeopleIcon /> },
      { label: 'Provider Health', path: '/admin/provider-health', icon: <HealthAndSafetyIcon /> },
      { label: 'Shops', path: '/admin/shops', icon: <StoreIcon /> },
      { label: 'Subscriptions', path: '/admin/subscriptions', icon: <CreditCardIcon /> },
      { label: 'Payments', path: '/admin/payments', icon: <PaymentIcon /> },
      { label: 'Reviews', path: '/admin/reviews', icon: <StarIcon /> },
      { label: 'Image Review', path: '/admin/image-review', icon: <ShieldIcon /> },
      { label: 'Service Categories', path: '/admin/service-categories', icon: <ContentCutIcon /> },
      { label: 'Service Templates', path: '/admin/service-templates', icon: <CollectionsIcon /> },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Audit Logs', path: '/admin/audit-logs', icon: <HistoryIcon /> },
    ],
  },
];

export const AdminShell: FC = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();

  const adminLabel = user?.name || user?.email || 'Admin';
  const adminInitial = adminLabel.charAt(0).toUpperCase();
  const environmentLabel = (
    import.meta.env.VITE_APP_ENV ||
    import.meta.env.MODE ||
    'local'
  ).toUpperCase();

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo / Brand */}
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', minHeight: adminTokens.appBarHeight }}>
        <Typography variant="h5" fontWeight={700} color="primary.main">
          Donvio
        </Typography>
      </Box>

      <Divider sx={{ borderColor: adminTokens.borderDivider }} />

      {/* Navigation Groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {navGroups.map((group) => (
          <List
            key={group.label}
            disablePadding
            sx={{ mb: 1.5, py: 0 }}
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  pl: 3,
                  mb: 1,
                  py: 0,
                  lineHeight: '24px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: adminTokens.nav.groupLabelColor,
                  bgcolor: 'transparent',
                  textTransform: 'capitalize',
                }}
              >
                {group.label}
              </ListSubheader>
            }
          >
            {group.items.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItemButton
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  selected={active}
                  sx={{
                    py: adminTokens.nav.itemPaddingY,
                    pl: `${adminTokens.nav.levelIndent}px`,
                    pr: adminTokens.nav.itemPaddingX,
                    mb: 0,
                    borderRight: active ? adminTokens.nav.activeBorder : '2px solid transparent',
                    borderColor: active ? adminTokens.nav.activeBorderColor : 'transparent',
                    bgcolor: active ? adminTokens.nav.activeBg : 'transparent',
                    color: active ? adminTokens.nav.activeColor : adminTokens.nav.inactiveColor,
                    '&:hover': {
                      bgcolor: active ? adminTokens.nav.activeBg : adminTokens.nav.hoverBg,
                    },
                    '&.Mui-selected': {
                      bgcolor: adminTokens.nav.activeBg,
                      '&:hover': {
                        bgcolor: adminTokens.nav.activeBg,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      color: active ? adminTokens.nav.activeColor : adminTokens.nav.inactiveColor,
                      '& .MuiSvgIcon-root': { fontSize: adminTokens.nav.iconSize },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.875rem',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: adminTokens.bgPage }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: adminTokens.sidebarWidth,
              bgcolor: adminTokens.bgSidebar,
              borderRight: `1px solid ${adminTokens.borderLight}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: collapsed ? 0 : adminTokens.sidebarWidth,
            flexShrink: 0,
            transition: 'width 0.2s ease',
            '& .MuiDrawer-paper': {
              width: adminTokens.sidebarWidth,
              bgcolor: adminTokens.bgSidebar,
              borderRight: `1px solid ${adminTokens.borderLight}`,
              boxShadow: 'none',
              transform: collapsed ? `translateX(-${adminTokens.sidebarWidth}px)` : 'none',
              transition: 'transform 0.2s ease',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content area */}
      <Box
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : collapsed ? '100%' : `calc(100% - ${adminTokens.sidebarWidth}px)`,
          transition: 'width 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* App Bar (Mantis style) */}
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{
            bgcolor: adminTokens.bgAppBar,
            borderBottom: `1px solid ${adminTokens.borderLight}`,
          }}
        >
          <Toolbar sx={{ minHeight: `${adminTokens.appBarHeight}px !important` }}>
            {isMobile ? (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <IconButton
                edge="start"
                onClick={() => setCollapsed(!collapsed)}
                sx={{ mr: 1 }}
              >
                {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Admin Portal
              </Typography>
              <Chip
                label={environmentLabel}
                size="small"
                color={environmentLabel === 'PRODUCTION' ? 'error' : 'default'}
                variant={environmentLabel === 'PRODUCTION' ? 'filled' : 'outlined'}
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                }}
              >
                {adminInitial}
              </Avatar>
              <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ maxWidth: 220 }}
                >
                  {adminLabel}
                </Typography>
                {user?.email && user.email !== adminLabel && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block', maxWidth: 220 }}
                  >
                    {user.email}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={<LogoutOutlinedIcon fontSize="small" />}
                onClick={logout}
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: { xs: 36, sm: 'auto' },
                  px: { xs: 1, sm: 1.5 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 0.75 },
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Sign out
                </Box>
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box sx={{ p: adminTokens.contentPadding, flex: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
